import { CellState, queenAttacks } from '../board';
import { RuleId, analyze, enumerateFrom, sigKey } from '../rules';
import {
  NF, PlayerParams, RateContext, SigVocab, countsOfAnalysis, dedupeRisk, featuresOf, logRate, markSubsumed,
} from './cost';
import { deserializeParams } from './fit';
import { buildGraph } from './graph';
import { profileGraph, requiredRules, requiredTechniques } from './profile';
import { simulate } from './simulate';
import { ANALYZER_VERSION } from './version';
import referenceJson from './reference.json';
import calibrationJson from './calibration.json';

// The one number the whole plan is aimed at: **how long this puzzle takes a person to solve**.
//
// Difficulty is quoted on the wall clock — reasoning plus the clicking, as one number, because
// that is what a player experiences. (It used to be quoted on a search-only "deduction clock" on
// the grounds that including the motor time made difficulty a proxy for board size. That was
// backwards: people reason *while* they click, a 9×9 really does take longer than a 7×7, and a
// number nobody can check against their own stopwatch is not worth defending.)
//
// The number is not the simulator's raw output. Measured against the leaderboard, raw `totalMs`
// runs about half of what people actually take and the gap widens with board size, so it goes
// through a calibration fitted on puzzles with at least three distinct players
// (`scripts/calibrate-solve-time.ts`): `refSolveMs` is a prediction of the **median player's
// solve time**, and lands within 23% of it (cross-validated median absolute error, held out by
// puzzle; the raw simulated total is 56% out). The simulator's job is to rank puzzles — which it
// does well, ρ ≈ 0.67 — and the calibration's job is to put that ranking on a human clock.
//
// Both halves are frozen and versioned: the fitted player in reference.json, the calibration in
// calibration.json. A refit MUST bump MODEL_VERSION rather than silently moving every published
// number — a difficulty scale that changes under people is worse than no scale.
//
// The *calibration* deliberately does NOT key the profile cache, even though it moves every
// published number. It is a pure function of the stored raw simulation (`refTotalMs` and `size`),
// so `withCurrentCalibration` re-derives the seconds on read exactly like `bandOf` re-derives the
// label, and a recalibration lands instantly on every puzzle at no cost. Putting it in the key
// instead invalidates the whole corpus, and re-profiling it means ~700 puzzles × ~1.5s of
// event-loop-blocking work before any puzzle gets its badge back — measured, on this codebase, by
// doing exactly that.

export const MODEL_VERSION: string = referenceJson.version;
export const CALIBRATION_VERSION: string = calibrationJson.version;

/** The frozen reference: the mean of the fitted population with every per-technique offset at 0. */
export const REFERENCE_PLAYER: PlayerParams = {
  theta: referenceJson.reference.theta,
  delta: new Float64Array(referenceJson.model.sigKeys.length),
  motor: referenceJson.reference.motor,
};

export const MODEL = deserializeParams(referenceJson.model);

/**
 * Simulated time → predicted median human solve time, in ms.
 *
 * `exp(a + b·ln(ms) + c·ln(size))`, fitted by `scripts/calibrate-solve-time.ts` against the
 * median player's time on every puzzle with 3+ distinct players. Two things it does: the
 * exponent `b < 1` pulls in a simulator that over-separates fast from slow puzzles, and `c`
 * restores the board-size cost its motor model under-counts. Both are measurements, not taste —
 * without them the number is 56% out and biased by size (median predicted/actual 1.29 at 7×7 and
 * 0.60 at 11×11); with them, 22.9% out and unbiased across sizes (0.96–1.12).
 */
export function predictedSolveMs(simTotalMs: number, size: number): number {
  const k = calibrationJson.sim;
  return Math.exp(k.a + k.b * Math.log(Math.max(1, simTotalMs)) + k.c * Math.log(size));
}

/** The same map for `cheapDifficulty`'s surrogate, fitted directly against the same target so
 *  the generator's energy function is in the same units as a target range. */
export function predictedSolveMsFromCheap(estimateMs: number, size: number): number {
  const k = calibrationJson.cheap;
  return Math.exp(k.a + k.b * Math.log(Math.max(1, estimateMs)) + k.c * Math.log(size));
}

/**
 * Difficulty bands, in predicted solve seconds.
 *
 * Cut points are the tertiles of the corpus (`scripts/calibrate-bands.ts`), not round numbers —
 * a band holding 2% of puzzles tells a player nothing. Three bands, because that is as fine a
 * distinction as a one-word label can honestly carry: the measured split is 30/38/32%, and the
 * bands' predicted medians (37/57/84s) track what players actually took on the puzzles enough of
 * them have played (33/58/78s).
 *
 * These now mean what they sound like: "hard" is a puzzle that takes a long time, and since
 * bigger boards do take longer, size is part of it — a 7×7 rarely reads hard and a 12×12 rarely
 * reads easy. That is the intended reading of a *time* estimate; if you want "hard for its
 * size", compare `refSolveMs` against other puzzles of the same size, which the analysis page
 * is for. Recompute the cuts after a recalibration.
 */
export const BANDS = [
  { band: 'easy', maxMs: 45_000 },
  { band: 'medium', maxMs: 70_000 },
  { band: 'hard', maxMs: Infinity },
] as const;

export type DifficultyBand = typeof BANDS[number]['band'];

export function bandOf(refSolveMs: number): DifficultyBand {
  for (const b of BANDS) if (refSolveMs <= b.maxMs) return b.band;
  return 'hard';
}

export interface PuzzleDifficulty {
  engineVersion: string;
  modelVersion: string;
  calibrationVersion: string;
  size: number;
  /** false ⇒ the rule engine cannot finish it, and the numbers below describe how far it got. */
  solved: boolean;
  /** Predicted median player solve time, reasoning and clicking together — THE difficulty
   *  scalar, and the only one anything user-facing should ever quote. Derived, not stored: read
   *  it through `withCurrentCalibration`, which is what every read path does. */
  refSolveMs: number;
  /** The simulator's raw auto-Xs wall clock, before calibration — the durable half of the
   *  record, and what a recalibration is re-applied to. */
  refTotalMs: number;
  /** …and its search-only half, for the analysis page's benefit. Not a difficulty. */
  refDeductionMs: number;
  /** Monte-Carlo standard error of `refTotalMs`. Absent on profiles cached before the
   *  calibration existed, which is why `seMs` is optional. */
  refTotalSeMs?: number;
  band: DifficultyBand;
  /** Monte-Carlo standard error on refSolveMs — small print, but it stops anyone reading
   *  a 3% difference between two puzzles as meaningful when it is noise. Simulation noise only;
   *  the calibration's own ~23% spread against real players dwarfs it. */
  seMs?: number;
  depth: number;
  /** Elimination techniques the puzzle cannot be solved without (R8 excluded — see profile.ts). */
  requiredTechniques: RuleId[];
  hardestRequiredRule: RuleId | null;
  singleSupportFacts: number;
  techniqueSpectrum: Record<string, number>;
}

export interface DifficultyOptions {
  traces?: number;
  seed?: number;
  /** Skip the ablation pass (9 extra closures, up to ~4s at 12×12). */
  skipAblation?: boolean;
}

/**
 * Full difficulty profile for a puzzle. Costs one graph build plus the ablation closures, so it
 * is computed offline and cached — never on a request path.
 */
export function computeDifficulty(colorRegions: string[][], opts: DifficultyOptions = {}): PuzzleDifficulty {
  const graph = buildGraph(colorRegions);
  const structural = profileGraph(graph);
  const traces = opts.traces ?? 24;
  const seed = opts.seed ?? 1;

  // Auto-Xs only. The two modes differ by about a second of simulated time — far inside the
  // calibration's own error, and the calibration was fitted over players using both — so a
  // separate manual number would be false precision at twice the cost.
  const auto = simulate(graph, MODEL, REFERENCE_PLAYER, { traces, seed, mode: 'autoXs' });

  let required: RuleId[] = [];
  let hardest: RuleId | null = null;
  if (!opts.skipAblation) {
    const ab = requiredRules(colorRegions);
    required = requiredTechniques(ab.required);
    hardest = required.length
      ? required.reduce((a, b) => (ruleWeight(b) > ruleWeight(a) ? b : a))
      : null;
  }

  // Through the same derivation the read paths use, so a freshly computed profile and a cached
  // one can never disagree about what the current calibration says.
  return withCurrentCalibration({
    engineVersion: ANALYZER_VERSION,
    modelVersion: MODEL_VERSION,
    calibrationVersion: CALIBRATION_VERSION,
    size: colorRegions.length,
    solved: graph.solved,
    refTotalMs: auto.totalMs,
    refDeductionMs: auto.deductionMs,
    refTotalSeMs: auto.totalSeMs,
    depth: structural.depth,
    requiredTechniques: required,
    hardestRequiredRule: hardest,
    singleSupportFacts: structural.singleSupportFacts,
    techniqueSpectrum: structural.techniqueSpectrum,
  });
}

/** Everything a profile records that does not depend on the calibration — what actually gets
 *  stored, and what a recalibration is re-applied to. */
export type RawProfile = Omit<PuzzleDifficulty, 'refSolveMs' | 'band' | 'seMs' | 'calibrationVersion'>
  & Partial<PuzzleDifficulty>;

/**
 * Put a profile on the current calibration: `refSolveMs`, its band and its standard error are all
 * derived here, from the stored raw simulation, on every read.
 *
 * This is the reason a recalibration costs nothing. The alternative — keying the profile cache on
 * the calibration version — invalidates all ~700 cached profiles, and since `computeDifficulty`
 * blocks the event loop for a second or more per puzzle, the whole site crawls for the ~20 minutes
 * it takes to earn them back, with no difficulty badge anywhere until it does.
 */
export function withCurrentCalibration(p: RawProfile): PuzzleDifficulty {
  const refSolveMs = predictedSolveMs(p.refTotalMs, p.size);
  return {
    ...p,
    calibrationVersion: CALIBRATION_VERSION,
    refSolveMs,
    band: bandOf(refSolveMs),
    // The calibration is a power of the simulated time, so the standard error carries through it
    // as `b · solve/total · se` rather than unchanged — it compresses along with everything else.
    // Undefined for profiles cached before the simulator reported a standard error on the total.
    seMs: p.refTotalSeMs === undefined
      ? undefined
      : calibrationJson.sim.b * (refSolveMs / Math.max(1, p.refTotalMs)) * p.refTotalSeMs,
  };
}

/** Local copy of profile.ts's spotting-difficulty order, to avoid importing it for one lookup. */
function ruleWeight(rule: RuleId): number {
  if (rule === 'R8') return 0;
  if (rule === 'R9') return 3;
  if (rule === 'R5' || rule === 'R6') return 2;
  return 1;
}

// ---------------------------------------------------------------------------
// The cheap surrogate
// ---------------------------------------------------------------------------

/**
 * A difficulty estimate cheap enough to sit inside a search loop.
 *
 * `computeDifficulty` costs a graph build, nine ablation closures and two Monte-Carlo
 * simulations — about 1.5s. A guided MCMC evaluates its objective thousands of times, so it
 * needs something three orders of magnitude cheaper. This runs the closure once, with no graph,
 * no premise minimization and no sampling, and estimates search time directly from the fitted
 * rates: at each round the available deductions compete at `exp(θ + α[sig])`, so the expected
 * wait for the next one is `1/Σλ`, and a round yielding `k` facts costs about `k/Σλ`.
 *
 * ── MEASURED, so you know what you are getting ──
 * Against `computeDifficulty` over 80 corpus puzzles: **49ms vs 302ms (6× faster), Spearman
 * ρ = 0.64, Pearson r = 0.18**. That is good enough to *rank* candidates roughly and to reject
 * obvious misses, and NOT good enough to steer a fine-grained time target — the plan hoped for a
 * <5ms energy for a guided MCMC, and this is not it. The three history features (proximity,
 * same-unit, recency) are zeroed because they need a trace, and the estimate is a Riemann
 * approximation to a shrinking risk set rather than a simulation.
 *
 * Against *players*, though, it is not the poor relation it looks: calibrated through
 * `predictedSolveMsFromCheap` it lands within 20.5% of observed median solve times, which is if
 * anything slightly better than the full simulator's 22.9% (n=205, cross-validated). The
 * surrogate's error and the simulator's are largely the same error — both are ranking the same
 * structure — and the calibration absorbs it either way.
 *
 * What it *is* exact about is structure: `solved`, `usedRules` and `soleCarriers` come straight
 * out of the closure and are not estimates. Structural targets can be steered with this;
 * time targets need rejection sampling against the real thing.
 */
export interface CheapProfile {
  solved: boolean;
  rounds: number;
  /** Raw search-time surrogate, on the simulator's deduction scale. Pass it through
   *  `predictedSolveMsFromCheap` to compare it with anything quoted in human seconds. */
  estimateMs: number;
  /** Techniques the closure actually used. A superset of what the puzzle *requires*. */
  usedRules: Set<RuleId>;
  usedSigs: Set<string>;
  /** Rules that were the only thing making progress in some round — strong evidence the puzzle
   *  requires them, at no extra cost. Ablation is still the exact answer. */
  soleCarriers: Set<RuleId>;
}

/**
 * Neutral attention state. The three history features (proximity to the last mark, same-unit,
 * recency) need a trace, which is the thing this deliberately does not simulate; zeroing them
 * costs accuracy but keeps every other fitted weight — including `subsumed` and `logLive`, the
 * two largest — doing real work.
 */
function neutralContext(A: ReturnType<typeof analyze>, marked: number, live: number): RateContext {
  return {
    A: countsOfAnalysis(A),
    markedCount: marked,
    lastCell: null,
    liveCount: live,
    unitLastUsed: EMPTY_MAP,
    sigLastUsed: EMPTY_MAP,
    burstIndex: 0,
  };
}
const EMPTY_MAP: Map<string, number> = new Map();

export function cheapDifficulty(colorRegions: string[][]): CheapProfile {
  const size = colorRegions.length;
  const board: CellState[][] = Array.from({ length: size }, () => Array<CellState>(size).fill(null));
  const vocab = SigVocab.from(MODEL.sigKeys);
  const theta = REFERENCE_PLAYER.theta;
  const x = new Float32Array(NF);

  const usedRules = new Set<RuleId>();
  const usedSigs = new Set<string>();
  const soleCarriers = new Set<RuleId>();
  let estimateMs = 0;
  let rounds = 0;
  let queens = 0;
  let marked = 0;

  for (let round = 0; round < size * size; round++) {
    // Queen reach is free under the canonical Auto-Xs semantics.
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (board[r][c] !== 'queen') continue;
        for (const k of queenAttacks(r, c, colorRegions)) {
          const [ar, ac] = k.split(',').map(Number);
          if (board[ar][ac] === null) { board[ar][ac] = 'X'; marked++; }
        }
      }
    }

    // Enumerate everything, with no cheap/expensive tiering. Tiering is a *presentation*
    // decision in the hints panel — it decides what to show a stuck player. The simulator has
    // every available deduction in its risk set simultaneously, so tiering here made the
    // surrogate disagree with the thing it is a surrogate for: measured rank correlation
    // against computeDifficulty went from 0.46 with tiering to 0.64 without.
    const A = analyze(board, colorRegions);
    const insts = enumerateFrom(A).filter(i => yieldsSomethingNew(board, i));
    if (insts.length === 0) break;

    const live = dedupeRisk(insts);
    const subsumed = markSubsumed(live);
    const ctx = neutralContext(A, marked, live.length);
    let sumLam = 0;
    const carriers = new Set<RuleId>();
    for (let i = 0; i < live.length; i++) {
      const key = sigKey(live[i].sig);
      usedSigs.add(key);
      usedRules.add(live[i].sig.rule);
      carriers.add(live[i].sig.rule);
      featuresOf(live[i], ctx, subsumed[i], x, 0);
      sumLam += Math.exp(Math.max(-30, Math.min(20, logRate(MODEL.alpha, MODEL.beta, theta, null, vocab.idOf(key), x, 0))));
    }
    if (carriers.size === 1) for (const r of carriers) soleCarriers.add(r);

    const newFacts = countNewFacts(board, insts);
    // Each fact is one decision, and the rate is roughly flat within a round, so a round
    // yielding k facts costs about k/Σλ. That is a Riemann approximation to the simulator's
    // shrinking risk set, which is exactly the accuracy this trades away for speed.
    if (sumLam > 0) estimateMs += (1000 * newFacts) / sumLam;

    for (const inst of insts) {
      for (const cellK of inst.cells) {
        const [r, c] = cellK.split(',').map(Number);
        if (board[r][c] !== null) continue;
        board[r][c] = inst.kind === 'place' ? 'queen' : 'X';
        marked++;
        if (inst.kind === 'place') queens++;
      }
    }
    rounds++;
    if (queens >= size) break;
  }

  return { solved: queens >= size, rounds, estimateMs, usedRules, usedSigs, soleCarriers };
}

function yieldsSomethingNew(board: CellState[][], inst: { cells: string[] }): boolean {
  for (const k of inst.cells) {
    const [r, c] = k.split(',').map(Number);
    if (board[r][c] === null) return true;
  }
  return false;
}

function countNewFacts(board: CellState[][], insts: Array<{ cells: string[] }>): number {
  const seen = new Set<string>();
  for (const inst of insts) {
    for (const k of inst.cells) {
      const [r, c] = k.split(',').map(Number);
      if (board[r][c] === null) seen.add(k);
    }
  }
  return seen.size;
}
